import axiosClient from '../axios-client';
// Import the types from the types file
import { CompanyInfo, CompanyInfoJson } from './types';

// Export the CompanyInfo type for use in other files
export type { CompanyInfo };

// Set DEBUG to false to disable verbose logging
const DEBUG = false;

// Helper function for conditional logging
const debugLog = (...args: any[]) => {
  if (DEBUG && process.env.NODE_ENV === 'development') {
    console.log(...args);
  }
};

// Cache for the loaded companies data
let companiesCache: Map<string, CompanyInfo> | null = null;

// Pre-indexed map of company names to their industries (for efficient lookup)
let companyIndustriesMap: Record<string, string[]> = {};
let isCompanyDataPreloaded = false;

/**
 * Fetch company information by company name
 * @param companyName Name of the company to fetch info for
 * @returns Company information if found
 */
const getCompanyInfo = async (companyName: string): Promise<CompanyInfo | null> => {
  try {
    if (!companyName) {
      return null;
    }
    
    // Use the local data instead of making an API call
    const companiesData = await getCompaniesData();
    
    // Normalize company name for case-insensitive comparison
    const normalizedName = companyName.toLowerCase();
    
    // Try to find the company in our local data
    const company = companiesData.get(normalizedName);
    
    if (company) {
      return company;
    }
    
    // If not found in local data, fallback to API call as a last resort
    try {
      const response = await axiosClient.get(`/companies-info`, {
        params: { name: companyName }
      });
      
      if (response.data && typeof response.data === 'object') {
        // Process company info to ensure consistent industries format
        const companyInfo = response.data;
        
        // Handle industries array - convert to array if it's a string or undefined
        if (!companyInfo.industries) {
          // If no industries array exists but industry single field exists, use that
          if (companyInfo.industry) {
            companyInfo.industries = [companyInfo.industry];
          } else {
            companyInfo.industries = [];
          }
        } else if (typeof companyInfo.industries === 'string') {
          // If industries is a single string, convert to array
          companyInfo.industries = [companyInfo.industries];
        }
        
        // If industry_tags is available but industries isn't, use industry_tags
        if (companyInfo.industry_tags && Array.isArray(companyInfo.industry_tags) && 
            (!companyInfo.industries || companyInfo.industries.length === 0)) {
          companyInfo.industries = companyInfo.industry_tags;
        }
        
        return companyInfo;
      }
    } catch (apiError) {
      // API fallback failed silently
    }
    
    return null;
  } catch (error: any) {
    if (error.response?.status === 404) {
      // Not found - no need to log this
    } else {
      console.error('Error fetching company info:', error);
    }
    return null;
  }
};

/**
 * Preload and index all company industries for fast access
 * This should be called during app initialization
 */
const preloadCompanyIndustries = async (): Promise<void> => {
  if (isCompanyDataPreloaded) {
    return; // Already preloaded
  }
  
  try {
    debugLog('Preloading company industry data...');
    // Get all companies data
    const companiesData = await getCompaniesData();
    
    // Create indexed map for fast lookups
    companiesData.forEach((company, normalizedName) => {
      // Use the original company name case from the data
      const originalName = company.name;
      
      // Determine industries from available data
      let industries: string[] = [];
      
      if (company.industries && Array.isArray(company.industries) && company.industries.length > 0) {
        industries = company.industries;
      } else if (company.industry_tags && Array.isArray(company.industry_tags)) {
        industries = company.industry_tags;
      } else if (company.industry) {
        industries = [company.industry];
      }
      
      // Store in our indexed map using original company name as key for exact matching
      companyIndustriesMap[originalName] = industries;
      
      // Also store with normalized key for case-insensitive matching
      companyIndustriesMap[normalizedName] = industries;
    });
    
    isCompanyDataPreloaded = true;
    
    // Only log a simple success message in production
    if (process.env.NODE_ENV === 'production') {
      console.log('Company data preloaded successfully');
    } else {
      // More detailed logging for development with DEBUG flag
      debugLog(`Company industry data preloaded successfully (${Object.keys(companyIndustriesMap).length} companies)`);
      
      // Log a few examples to help debugging
      if (DEBUG) {
        const companyNames = Object.keys(companyIndustriesMap);
        if (companyNames.length > 0) {
          debugLog('Sample of indexed companies:');
          for (let i = 0; i < Math.min(5, companyNames.length); i++) {
            const name = companyNames[i];
            debugLog(`- "${name}": ${companyIndustriesMap[name].length} industries`);
          }
        }
      }
    }
  } catch (error) {
    console.error('Error preloading company industries:', error);
  }
};

/**
 * Get industries for a company from the preloaded data
 * This is a synchronous operation since data is already in memory
 */
const getCompanyIndustriesSync = (companyName: string): string[] => {
  // Try exact match first
  if (companyIndustriesMap[companyName]) {
    return companyIndustriesMap[companyName];
  }
  
  // Try normalized match
  const normalizedName = companyName.toLowerCase();
  if (companyIndustriesMap[normalizedName]) {
    return companyIndustriesMap[normalizedName];
  }
  
  // Fallback to empty array if company not found
  return [];
};

/**
 * Get industries for a batch of companies from the preloaded data
 * This is a synchronous operation that returns immediately
 */
const getCompanyIndustriesBatch = (companyNames: string[]): Record<string, string[]> => {
  const result: Record<string, string[]> = {};
  
  if (!isCompanyDataPreloaded) {
    console.warn('Company data not preloaded yet - industry data will be incomplete');
  }
  
  // Only log this in development environment with DEBUG flag
  debugLog(`Looking up industries for ${companyNames.length} companies`);
  
  let foundCount = 0;
  
  companyNames.forEach(companyName => {
    // Try exact match first
    if (companyIndustriesMap[companyName]) {
      result[companyName] = companyIndustriesMap[companyName];
      foundCount++;
      return;
    }
    
    // Try normalized match
    const normalizedName = companyName.toLowerCase();
    if (companyIndustriesMap[normalizedName]) {
      result[companyName] = companyIndustriesMap[normalizedName];
      foundCount++;
      return;
    }
    
    // Fallback: no industries found
    result[companyName] = [];
  });
  
  // Only log this in development environment with DEBUG flag
  debugLog(`Found industries for ${foundCount} out of ${companyNames.length} companies`);
  
  return result;
};

/**
 * Get industries for a batch of companies
 * @param companyNames Array of company names
 * @returns Map of company names to their industries
 */
const getCompanyIndustries = async (companyNames: string[]): Promise<Record<string, string[]>> => {
  try {
    if (!companyNames || !Array.isArray(companyNames) || companyNames.length === 0) {
      return {};
    }
    
    // If data is already preloaded, use the synchronous batch function
    if (isCompanyDataPreloaded) {
      return getCompanyIndustriesBatch(companyNames);
    }
    
    // Get the companies data from the local cache
    const companiesData = await getCompaniesData();
    
    // Create map of company name -> industries
    const industriesMap: Record<string, string[]> = {};
    
    // Process each company
    companyNames.forEach(companyName => {
      const normalizedName = companyName.toLowerCase();
      const company = companiesData.get(normalizedName);
      
      if (company) {
        // Use industries or industry_tags if available
        if (company.industries && Array.isArray(company.industries) && company.industries.length > 0) {
          industriesMap[companyName] = company.industries;
        } else if (company.industry_tags && Array.isArray(company.industry_tags)) {
          industriesMap[companyName] = company.industry_tags;
        } else if (company.industry) {
          industriesMap[companyName] = [company.industry];
        } else {
          industriesMap[companyName] = [];
        }
      } else {
        industriesMap[companyName] = [];
      }
    });
    
    return industriesMap;
  } catch (error) {
    console.error('Error getting company industries:', error);
    return {};
  }
};

/**
 * Load and cache the companies data from the local JSON file
 * Only loads the data once and reuses it for subsequent calls
 */
const getCompaniesData = async (): Promise<Map<string, CompanyInfo>> => {
  if (companiesCache) {
    return companiesCache;
  }
  
  try {
    // Dynamically import the JSON file (ensures it's in a separate chunk)
    debugLog('Loading companies data from JSON file...');
    let companies: CompanyInfoJson[] = [];
    
    try {
      const companiesModule = await import('./companies_info.json');
      const importedData = companiesModule.default || companiesModule;
      
      if (Array.isArray(importedData)) {
        companies = importedData;
        debugLog(`Successfully loaded companies data: ${companies.length} entries`);
      } else {
        console.error('Imported companies data is not an array:', importedData);
      }
    } catch (importError) {
      console.error('Error importing companies JSON file:', importError);
      // Fallback to empty array if import fails
    }
    
    // Create a map for fast lookups by name
    const companiesMap = new Map<string, CompanyInfo>();
    
    // Log a sample entry to help with debugging
    if (companies.length > 0 && DEBUG) {
      debugLog('Sample company entry:', companies[0]);
    }
    
    companies.forEach((company: CompanyInfoJson) => {
      // Skip entries without a valid name
      if (!company.name) {
        console.warn('Skipping company entry without a name:', company);
        return;
      }
      
      // Normalize the company name for case-insensitive lookups
      const normalizedName = company.name.toLowerCase();
      
      // Convert to our CompanyInfo format
      companiesMap.set(normalizedName, {
        _id: normalizedName,  // Use name as ID since we don't have real IDs
        name: company.name,
        description: company.description || '',
        website_url: company.website_url || '',
        industry: '', // Not used in this format
        industries: company.industry_tags || [],
        industry_tags: company.industry_tags || []
      });
    });
    
    debugLog(`Created company map with ${companiesMap.size} entries`);
    
    // Cache the result
    companiesCache = companiesMap;
    return companiesMap;
  } catch (error) {
    console.error('Error loading companies data:', error);
    // Return empty map on error
    return new Map();
  }
};

export const companyInfoService = {
  getCompanyInfo,
  getCompanyIndustries,
  preloadCompanyIndustries,
  getCompanyIndustriesSync,
  getCompanyIndustriesBatch
};

export default companyInfoService; 